import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useCallback, useEffect } from 'react';
import { Button, Space, Tooltip, Divider } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TokenChip } from './tokenChipExtension';
import { TextAlign } from './textAlignExtension';
import TokenChips from './TokenChips';
import './tiptap-editor.css';

function MenuBar({ editor }) {
  const { t } = useTranslation('forms');
  if (!editor) return null;
  return (
    <div className="tiptap-toolbar">
      <Space size={4} wrap>
        <Tooltip title={t('editorBold')}>
          <Button
            size="small"
            type={editor.isActive('bold') ? 'primary' : 'default'}
            icon={<BoldOutlined />}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
        </Tooltip>
        <Tooltip title={t('editorItalic')}>
          <Button
            size="small"
            type={editor.isActive('italic') ? 'primary' : 'default'}
            icon={<ItalicOutlined />}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
        </Tooltip>
        <Divider type="vertical" />
        <Tooltip title={t('editorHeading2')}>
          <Button
            size="small"
            type={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </Button>
        </Tooltip>
        <Tooltip title={t('editorHeading3')}>
          <Button
            size="small"
            type={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </Button>
        </Tooltip>
        <Divider type="vertical" />
        <Tooltip title={t('editorBulletList')}>
          <Button
            size="small"
            type={editor.isActive('bulletList') ? 'primary' : 'default'}
            icon={<UnorderedListOutlined />}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
        </Tooltip>
        <Tooltip title={t('editorOrderedList')}>
          <Button
            size="small"
            type={editor.isActive('orderedList') ? 'primary' : 'default'}
            icon={<OrderedListOutlined />}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
        </Tooltip>
        <Divider type="vertical" />
        <Tooltip title={t('editorAlignLeft')}>
          <Button
            size="small"
            type={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
            icon={<AlignLeftOutlined />}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          />
        </Tooltip>
        <Tooltip title={t('editorAlignCenter')}>
          <Button
            size="small"
            type={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
            icon={<AlignCenterOutlined />}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          />
        </Tooltip>
        <Tooltip title={t('editorAlignRight')}>
          <Button
            size="small"
            type={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
            icon={<AlignRightOutlined />}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          />
        </Tooltip>
        <Tooltip title={t('editorAlignJustify')}>
          <Button
            size="small"
            type={
              editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'
            }
            icon={<MenuOutlined />}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          />
        </Tooltip>
      </Space>
    </div>
  );
}

/**
 * TipTap editor for building document templates. Mirrors the shared
 * `TiptapEditor` but adds the atomic `TokenChip` node and a clickable token
 * palette. `tokens` = [{ token }] fixed tokens; `customFieldKeys` = ad-hoc field
 * keys defined on the template. Emits HTML (with `{{token}}` chips) via onChange.
 * `showPalette={false}` hides the palette when editing an already filled
 * document, where there is nothing left to insert. `showSignature={false}`
 * drops the signature hint (an email has no signature block).
 */
export default function DocumentTemplateEditor({
  value,
  onChange,
  placeholder,
  tokens = [],
  customFieldKeys = [],
  showPalette = true,
  showSignature = true,
}) {
  const { t } = useTranslation('forms');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Image,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder || '' }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TokenChip,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      // TipTap 3 takes an options object here; a bare `false` still emitted an
      // update, so loading a saved template marked it as edited.
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  const insertToken = useCallback(
    (token) => {
      if (!editor) return;
      editor.chain().focus().insertToken(token).run();
    },
    [editor]
  );

  return (
    <div className="tiptap-editor-wrapper">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="tiptap-content" />

      {showSignature && (
        <div className="tiptap-signature-preview">
          <span>{t('autoSignatureHint')}</span>
          <div className="tiptap-signature-preview__block">
            <span className="token-chip">{'{{directorTitle}}'}</span>
            <span>_____________________</span>
            <span className="token-chip">{'{{directorName}}'}</span>
          </div>
        </div>
      )}

      {showPalette && (
        <div className="tiptap-token-palette" style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>
            {t('customTemplateTokens.paletteTitle', 'Oznake')}
          </div>
          <TokenChips
            tokens={tokens}
            customFieldKeys={customFieldKeys}
            onInsert={insertToken}
          />
        </div>
      )}
    </div>
  );
}
